import { createTheme } from "@mui/material/styles";

export const authTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#FFFFFF",
    },
    custom: {
      disabledBackground: "",
      yellow: "",
      orange: "",
      white: "",
      red: {
        main: "",
        pale: "",
      },
      blue: {
        light: "",
        main: "",
        dark: "",
        pale: "",
      },
      grey: {
        neutral: "",
        dark: "",
        warm: "",
        cool: "",
      },
      chartColors: [],
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&:not(.Mui-focused):not(.MuiFormLabel-filled)": {
            transform: "translate(14px, 10px) scale(1)", // 10px matches input vertical padding
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: "15px",
          color: "#FFFFFF",
          borderRadius: "6px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FFFFFF33",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FFFFFF66",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FFFFFF",
          },
          "&.Mui-error": {
            color: "#FF9172",
            "& .MuiInputBase-input::placeholder": {
              color: "#FF9172",
              opacity: 1,
            },
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FF9172",
            backgroundColor: "#FF917214",
          },
          "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FF9172",
            backgroundColor: "#FF917214",
          },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FF9172",
            backgroundColor: "#FF917214",
          },
        },
        input: {
          padding: "10px 12px",
          lineHeight: "20px",
          "&:-webkit-autofill": {
            "-webkit-box-shadow": "none",
            "color-scheme": "dark",
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: "4px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          padding: "10px 12px",
          lineHeight: "20px",
          "&.Mui-disabled": {
            cursor: "not-allowed",
            pointerEvents: "all",
          },
        },
        outlined: {
          backgroundColor: "#FFFFFF",
          color: "#000000",
          borderColor: "#FFFFFF33",
          "&:hover": {
            backgroundColor: "#FFFFFFDD",
            borderColor: "#FFFFFFDD",
          },
        },
        containedSecondary: {
          color: "white",
          borderRadius: "36px",
          padding: "8px 16px",
          fontWeight: "450",
          fontSize: "14px",
          lineHeight: "20px",
          textAlign: "center",
          backgroundColor: "#FFFFFF0A",
          boxShadow: "0px 1px 3px 0px #0000001A, 0px 1px 2px -1px #0000001A",
          position: "relative",
          overflow: "hidden",
          fontFamily: "Inter, sans-serif",
          "&:hover": {
            backgroundColor: "#FFFFFF14",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "36px",
            padding: "1px",
            background:
              "linear-gradient(180deg, rgba(250, 250, 250, 0.2) 0%, rgba(250, 250, 250, 0.05) 16%, rgba(250, 250, 250, 0.05) 86%, rgba(255, 255, 255, 0) 100%)",
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
            WebkitMaskComposite: "destination-out",
            pointerEvents: "none",
            opacity: 1,
            transition: "opacity 0.2s ease",
          },
          "&:hover::after": {
            opacity: 0,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          width: "312px",
          backgroundImage: "none",
          gap: "40px",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          textAlign: "center",
          padding: "0",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "0",
          "&:last-child": {
            paddingBottom: "0",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#FFFFFF99",
        },
        h4: {
          color: "#FFFFFF",
          fontWeight: 600,
          lineHeight: "110%",
          fontSize: "32px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          color: "#FFFFFF99",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          fontWeight: 450,
        },
      },
    },
    MuiLoadingButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "6px",
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: {
            color: "#000000",
            backgroundColor: "#CBFC69",
            boxShadow: "0px 1px 3px 0px #0000001A, 0px 1px 2px -1px #0000001A",
            "&:hover": {
              backgroundColor: "#CBFC69",
              boxShadow:
                "0px 1px 3px 0px #0000001A, 0px 1px 2px -1px #0000001A, 0px 0px 8px 0px #BBF65680",
            },
          },
        },
        {
          props: { variant: "outlined", color: "secondary" },
          style: {
            backgroundColor: "#FFFFFF1A",
            color: "#FFFFFF99",
            border: "1px solid #FFFFFF33",
            "&:hover": {
              backgroundColor: "#FFFFFF33",
              borderColor: "#FFFFFF33",
            },
          },
        },
      ],
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: "4px 0 0 0",
          "&.Mui-error": {
            color: "#FF9172",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#050B09",
          borderRadius: "6px",
          padding: "10px 12px",
          border: "1px solid #27272A",
          fontSize: "14px",
          lineHeight: "100%",
          fontWeight: 400,
          color: "#FAFAFA",
          fontFamily: "Inter, sans-serif",
        },
      },
      defaultProps: {
        slotProps: {
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
        },
      },
    },
  },
});
